import { httpClient } from "@/app/client/httpClient";
import { captureException } from "@/plugins/sentry";
import { authStore } from "@/store/auth";

function digestToStr(digest: ArrayBuffer) {
  return Array.from(new Uint8Array(digest))
    .map(v => v.toString(16).padStart(2, "0"))
    .join("");
}
function strToDigest(hexStr: string) {
  if (hexStr.length % 2 !== 0) {
    throw new Error("Invalid hexadecimal string");
  }
  const digest = new Uint8Array(hexStr.length / 2);
  for (let i = 0; i < hexStr.length; i += 2) {
    const hexByte = hexStr.substring(i, i + 2);
    digest[i / 2] = parseInt(hexByte, 16);
  }
  return digest;
}
async function makeDigest(str: string) {
  return await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
}
class AbTestService {
  registerEvent(
    testKey: string,
    userKey: string,
    eventType: string,
    segmentNumber: number,
    additionalData?: string
  ) {
    return httpClient.system.abtest.register_event
      .$post({
        body: {
          testKey,
          userKey,
          eventType,
          segmentNumber,
          additionalData
        }
      })
      .catch(err => {
        // エラーを握りつぶす
        captureException(err);
      });
  }
  async getSegmentNumber(testKey: string, userKey: string) {
    // 1回目のハッシュ
    const hash1 = digestToStr(await makeDigest(userKey + testKey));
    const hash2 = digestToStr(await makeDigest(hash1));
    // ArrayBufferを整数に変換
    const digest = strToDigest(hash2);
    const hashInt = new DataView(digest.buffer).getUint32(0);

    // 0-99の範囲に正規化
    return hashInt % 100;
  }
}
export const abTestService = new AbTestService();

// 各ABテストで利用する値は削除する際に簡単に見つけられるように以下にまとめて記載する
/*
サンプル
export const AbTest2608V2 = {
  testKey: "2608-2",
  eventTypes: {
    entry: "entry",
    completedSignup: "completed_signup",
    completedAddQualification: "completed_add_qualification",
    completedApplication: "completed_application",
    finishedWork: "finished_work"
  },
  threshold: 50,
  isTestGroup: (segmentNumber?: number | null) => {
    if (segmentNumber === null || segmentNumber === undefined) {
      return false;
    }
    return segmentNumber >= AbTest2608V2.threshold;
  }
};
*/

export const AbTest3300 = {
  testKey: "3300-3",
  eventTypes: {
    entry: "entry", // 仕事詳細画面表示
    clickApplyButton: "click_apply_button", // 応募ボタン押下時
    completeApplication: "complete_application", // 応募完了
    completeWork: "complete_work" // 勤務完了
  },

  getTestGroup(): "B" | "C" {
    const segmentNumber = authStore.segmentNumber3300;
    if (!segmentNumber) {
      return "B";
    }

    // 50ずつ2つのグループに分ける
    // 0-49: B(50%), 50-99: C(50%)
    if (segmentNumber < 50) {
      return "B";
    }
    return "C";
  },

  registerEvent(eventType: string) {
    const segmentNumber = authStore.segmentNumber3300;
    if (
      !authStore.sessionId ||
      segmentNumber === null ||
      segmentNumber === undefined
    ) {
      return;
    }
    return abTestService.registerEvent(
      AbTest3300.testKey,
      authStore.sessionId,
      eventType,
      segmentNumber
    );
  }
};

export const AbTest2807 = {
  testKey: "2807",
  eventTypes: {
    entry: "entry", // 仕事詳細画面表示
    completeApplication: "complete_application" // 応募完了
  },

  getTestGroup(): "A" | "B" | "C" {
    const segmentNumber = authStore.segmentNumber2807;
    if (
      !authStore.sessionId ||
      segmentNumber === null ||
      segmentNumber === undefined
    ) {
      return "A";
    }

    // 33ずつ3つのグループに分ける
    // 0-33: A(34%), 34-66: B(33%), 67-99: C(33%)
    if (segmentNumber < 34) {
      return "A";
    }
    if (segmentNumber < 67) {
      return "B";
    }
    return "C";
  },

  registerEvent(eventType: string) {
    const segmentNumber = authStore.segmentNumber2807;
    if (
      !authStore.sessionId ||
      segmentNumber === null ||
      segmentNumber === undefined
    ) {
      return;
    }
    return abTestService.registerEvent(
      AbTest2807.testKey,
      authStore.sessionId,
      eventType,
      segmentNumber
    );
  }
};

export const AbTest3113 = {
  testKey: "3113-4",
  eventTypes: {
    entry: "entry", // オンボーディング画面表示
    entryHowToGuide: "entry_how_to_guide", // 使い方ガイド表示
    enterPhoneNumberNext: "enter_phone_number_next", // 電話番号を入力して次画面に遷移
    enterCodeNext: "enter_code_next", // 認証コードを入力して次画面に遷移
    enterPasswordNext: "enter_password_next", // パスワードを入力して次画面に遷移
    completeApplication: "complete_application", // 応募完了
    completeAccountApplication: "complete_account_application", // アカウント認証申請完了
    completeQualificationRegistration: "complete_qualification_registration" // 資格登録完了
  },

  getTestGroup(): "A" | "B" | "C" | "D" | "E" {
    // 薬剤師アプリの場合は常にBグループ
    if (authStore.isCaitechPharmacistProduct) {
      return "B";
    }

    const segmentNumber = authStore.segmentNumber3113;
    if (
      !authStore.sessionId ||
      segmentNumber === null ||
      segmentNumber === undefined
    ) {
      return "A";
    }

    // 25ずつ4つのグループに分ける
    // 0-24: A(25%), 25-49: C(25%), 50-74: D(25%), 75-99: E(25%)
    if (segmentNumber < 25) {
      return "A";
    }
    if (segmentNumber < 50) {
      return "C";
    }
    if (segmentNumber < 75) {
      return "D";
    }
    return "E";
  },

  registerEvent(eventType: string) {
    // 薬剤師アプリの場合はイベントを飛ばさない
    if (authStore.isCaitechPharmacistProduct) {
      return;
    }

    const segmentNumber = authStore.segmentNumber3113;
    if (
      !authStore.sessionId ||
      segmentNumber === null ||
      segmentNumber === undefined
    ) {
      return;
    }
    return abTestService.registerEvent(
      AbTest3113.testKey,
      authStore.sessionId,
      eventType,
      segmentNumber
    );
  }
};

export const AbTest3401 = {
  testKey: "3401",
  eventTypes: {
    entry: "entry", // 申込条件確認画面表示
    completeApplication: "complete_application", // 応募完了
    completeWork: "complete_work" // 勤務完了
  },

  getTestGroup(): "A" | "B" {
    const segmentNumber = authStore.segmentNumber3401;
    if (!segmentNumber) {
      return "A";
    }

    // 50ずつ2つのグループに分ける
    // 0-49: A(50%), 50-99: B(50%)
    if (segmentNumber < 50) {
      return "A";
    }
    return "B";
  },

  registerEvent(eventType: string) {
    const segmentNumber = authStore.segmentNumber3401;
    if (
      !authStore.sessionId ||
      segmentNumber === null ||
      segmentNumber === undefined
    ) {
      return;
    }
    return abTestService.registerEvent(
      AbTest3401.testKey,
      authStore.sessionId,
      eventType,
      segmentNumber
    );
  }
};
